import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {usePage} from "@inertiajs/react";
import axios from "axios";

/**
 * Zustand des einklappbaren Hauptmenüs (Paket S4, schmaler Symbol-Streifen
 * statt voller Seitenleiste - siehe docs/tasks/2026-09-18-cosmos-bereich/
 * KONZEPT-MENUE-EINKLAPPEN.md). Quelle der Wahrheit ist der geteilte
 * Inertia-Prop "menueEingeklappt" (Datenbankspalte users.menue_eingeklappt,
 * siehe HandleInertiaRequests); localStorage dient nur als Sofort-Start
 * gegen Flackern beim allerersten Rendern, bevor die Seite ihre Props hat.
 *
 * Die Fremdkomponente Sidebar.jsx (laravel-inertia-backend) wird dabei
 * nicht angefasst - eine Klasse an <body> steuert per CSS (resources/scss/
 * menue.scss) die Darstellung von außen.
 */

const STORAGE_KEY = "om_menue_eingeklappt";

const MenueZustandContext = createContext(null);

function ausLocalStorage() {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function inLocalStorageSpeichern(eingeklappt) {
    try {
        window.localStorage.setItem(STORAGE_KEY, eingeklappt ? "1" : "0");
    } catch {
        // localStorage kann fehlschlagen (privater Modus, volle Quota) -
        // der Klappzustand funktioniert dann trotzdem, nur ohne
        // Sofort-Start beim nächsten Laden.
    }
}

export function MenueZustandProvider({children}) {
    const {menueEingeklappt} = usePage().props;

    const [eingeklappt, setEingeklapptState] = useState(() =>
        typeof menueEingeklappt === "boolean" ? menueEingeklappt : ausLocalStorage()
    );

    // Der geteilte Prop bleibt die Quelle der Wahrheit - übernimmt bei jeder
    // Inertia-Navigation, falls er sich unterscheidet (z. B. nach Umschalten
    // in einem anderen Tab derselben Sitzung).
    useEffect(() => {
        if (typeof menueEingeklappt === "boolean" && menueEingeklappt !== eingeklappt) {
            setEingeklapptState(menueEingeklappt);
        }
        // eingeklappt bewusst nicht in den Deps - sonst würde jede eigene
        // Umschaltung sofort durch den (noch alten) Server-Prop überschrieben.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menueEingeklappt]);

    useEffect(() => {
        document.body.classList.toggle("menue-eingeklappt", eingeklappt);
        inLocalStorageSpeichern(eingeklappt);
    }, [eingeklappt]);

    // Beim Verlassen der Seite (z. B. Logout) die Klasse nicht auf dem Login-
    // Screen stehen lassen.
    useEffect(() => () => document.body.classList.remove("menue-eingeklappt"), []);

    const umschalten = useCallback(() => {
        setEingeklapptState((vorher) => {
            const neu = !vorher;

            axios.patch("/benutzer/einstellungen/menue", {eingeklappt: neu}).catch(() => {
                // Fehlgeschlagenes Speichern ist kein Blocker für die
                // Bedienung - die Oberfläche bleibt beim gewählten Zustand,
                // nur die Datenbank hinkt bis zum nächsten Umschalten hinterher.
            });

            return neu;
        });
    }, []);

    // Tastenkürzel "[" - nur außerhalb von Eingabefeldern/Editoren, damit
    // Texteingabe (u. a. der Tiptap-Editor der E-Mail-Kampagnen) unberührt bleibt.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey) {
                return;
            }

            const ziel = event.target;
            const editierbar =
                ziel instanceof HTMLElement &&
                (ziel.isContentEditable ||
                    ["INPUT", "TEXTAREA", "SELECT"].includes(ziel.tagName));

            if (editierbar) {
                return;
            }

            umschalten();
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [umschalten]);

    return (
        <MenueZustandContext.Provider value={{eingeklappt, umschalten}}>
            {children}
        </MenueZustandContext.Provider>
    );
}

export function useMenueZustand() {
    const context = useContext(MenueZustandContext);

    if (!context) {
        throw new Error("useMenueZustand muss innerhalb von MenueZustandProvider verwendet werden");
    }

    return context;
}
